// src/App.tsx
import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";

import { AuthProvider, useAuth } from "./context/AuthContext";
import { MixmindUniverse, useUniverse, UniverseProvider } from "./context/UniverseContext";

import Header from "./components/Header";
import { UniverseBackgroundOverlay } from "./components/universe/UniverseBackgroundOverlay";
import Footer from "./components/Footer";

import Home from "./pages/Home";
import FamilySpace from "./pages/FamilySpace";
import Account from "./pages/Account";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Confidentialite from "./pages/Confidentialite";
import MentionsLegales from "./pages/MentionsLegales";
import Roadmap from "./pages/Roadmap";
import Login from "./pages/Login";
import Premium from "./pages/Premium";
import AcademyHub from "./pages/AcademyHub";
import PlayHub from "./pages/PlayHub";
import AssistHub from "./pages/AssistHub";
import AcademyExplorer from "./pages/academy/AcademyExplorer";
import AcademyApprendre from "./pages/academy/AcademyApprendre";
import AcademyComprendre from "./pages/academy/AcademyComprendre";
import AcademyDecouvrir from "./pages/academy/AcademyDecouvrir";
import PlayMiniJeux from "./pages/play/PlayMiniJeux";
import PlayIllusions from "./pages/play/PlayIllusions";
import PlayDefis from "./pages/play/PlayDefis";
import PlayEntrainements from "./pages/play/PlayEntrainements";
import AssistCoaching from "./pages/assist/AssistCoaching";
import AssistIntrospection from "./pages/assist/AssistIntrospection";
import AssistAnalyse from "./pages/assist/AssistAnalyse";
import AssistDecisions from "./pages/assist/AssistDecisions";
import {
  ComprendreRedirect,
  PratiquerRedirect,
  ExplorerRedirect,
} from "./pages/LegacyRedirects";
import ThemePage from "./pages/theme/ThemePage";
import AcademyDashboard from "./pages/AcademyDashboard";
import ParcoursPage from "./pages/parcours/ParcoursPage";
import ParcoursPlayStep from "./pages/parcours/ParcoursPlayStep";
import ParcoursAssistStep from "./pages/parcours/ParcoursAssistStep";
import AcademyParcours from "./pages/academy/AcademyParcours";
import AcademyThemes from "./pages/academy/AcademyThemes";
import AcademyResources from "./pages/academy/AcademyResources";
import AcademyTeachers from "./pages/academy/AcademyTeachers";
import ScrollToTop from "./components/ScrollToTop";
import { ProgressionProvider } from "./context/ProgressionContext";
import { ParcoursProvider } from "./context/ParcoursContext";
import { AssistProvider } from "./context/AssistContext";
import BetaAccessGate from "./components/BetaAccessGate";
import { useBranding } from "./modules/brand/useBranding";

function AppLayout() {
  const { darkMode, isPremium, isPremiumTheme } = useAuth();
  const brand = useBranding();
  const [menuOpen, setMenuOpen] = useState(false);
  const { setUniverse } = useUniverse();
  const location = useLocation();

  // le thème suit désormais la préférence utilisateur (initialisée à true en premium)
  const effectiveDark = !!darkMode;
  const premiumVisual = brand.variant === "premium" || isPremiumTheme;

  // Appliquer le thème global sur <html> (pour Tailwind .dark et variables CSS)
  useEffect(() => {
    const root = document.documentElement;

    if (effectiveDark) {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }

    if (premiumVisual) {
      root.dataset.premium = "true";
    } else {
      delete root.dataset.premium;
    }
  }, [effectiveDark, premiumVisual]);

  useEffect(() => {
    const faviconHref = brand.faviconHref;
    const link =
      (document.querySelector("link[rel='icon']") as HTMLLinkElement | null) ||
      (document.querySelector("link[rel='shortcut icon']") as HTMLLinkElement | null);

    if (link) {
      link.href = faviconHref;
    } else {
      const newLink = document.createElement("link");
      newLink.rel = "icon";
      newLink.href = faviconHref;
      document.head.appendChild(newLink);
    }
  }, [brand.faviconHref]);

  useEffect(() => {
    const className = "mm-premium";
    if (brand.variant === "premium") {
      document.body.classList.add(className);
    } else {
      document.body.classList.remove(className);
    }

    return () => {
      document.body.classList.remove(className);
    };
  }, [brand.variant]);

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--mm-glow-gradient",
      brand.glowGradient
    );
  }, [brand.glowGradient]);

  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith("/academy") || path.startsWith("/theme") || path.startsWith("/parcours")) {
      setUniverse(MixmindUniverse.Academy);
    } else if (path.startsWith("/play")) {
      setUniverse(MixmindUniverse.Play);
    } else if (path.startsWith("/assist")) {
      setUniverse(MixmindUniverse.Assist);
    } else {
      setUniverse(MixmindUniverse.None);
    }
  }, [location.pathname, setUniverse]);

  const rootClass = `relative min-h-screen flex flex-col text-site z-10 ${
    effectiveDark && premiumVisual ? "premium-dark" : ""
  }`;

  return (
    <>
      <ScrollToTop />
      <UniverseBackgroundOverlay />
      <div className={rootClass}>
        <Header dark={effectiveDark} menuOpen={menuOpen} setMenuOpen={setMenuOpen} />

        <main className="flex-1 px-4 py-10 md:px-6 animate-fade-in">
          <Routes>
            <Route path="/" element={<Home />} />
            {/* Academy */}
            <Route path="/academy" element={<AcademyHub />} />
            <Route path="/academy/explorer" element={<AcademyExplorer />} />
            <Route path="/academy/apprendre" element={<AcademyApprendre />} />
            <Route path="/academy/comprendre" element={<AcademyComprendre />} />
            <Route path="/academy/decouvrir" element={<AcademyDecouvrir />} />
            <Route path="/academy/dashboard" element={<AcademyDashboard />} />
            <Route path="/academy/parcours" element={<AcademyParcours />} />
            <Route path="/academy/themes" element={<AcademyThemes />} />
            <Route path="/academy/resources" element={<AcademyResources />} />
            <Route path="/academy/teachers" element={<AcademyTeachers />} />

            {/* Play */}
            <Route path="/play" element={<PlayHub />} />
            <Route path="/play/minijeux" element={<PlayMiniJeux />} />
            <Route path="/play/illusions" element={<PlayIllusions />} />
            <Route path="/play/defis" element={<PlayDefis />} />
            <Route path="/play/entrainements" element={<PlayEntrainements />} />

            {/* Assist */}
            <Route path="/assist" element={<AssistHub />} />
            <Route path="/assist/coaching" element={<AssistCoaching />} />
            <Route path="/assist/introspection" element={<AssistIntrospection />} />
            <Route path="/assist/analyse" element={<AssistAnalyse />} />
            <Route path="/assist/decisions" element={<AssistDecisions />} />

            {/* Theme page */}
            <Route path="/theme/:themeId" element={<ThemePage />} />
            <Route path="/parcours/:parcoursId" element={<ParcoursPage />} />
            <Route path="/parcours/:parcoursId/play/:stepId" element={<ParcoursPlayStep />} />
            <Route path="/parcours/:parcoursId/assist/:stepId" element={<ParcoursAssistStep />} />

            {/* Redirections anciennes rubriques */}
            <Route path="/comprendre" element={<ComprendreRedirect />} />
            <Route path="/pratiquer" element={<PratiquerRedirect />} />
            <Route path="/explorer" element={<ExplorerRedirect />} />
            <Route path="/parents" element={<FamilySpace />} />
            <Route path="/profil" element={<Account />} />
            <Route path="/account" element={<Account />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/confidentialite" element={<Confidentialite />} />
            <Route path="/mentions-legales" element={<MentionsLegales />} />
            <Route path="/roadmap" element={<Roadmap />} />
            <Route path="/login" element={<Login />} />
            <Route path="/premium" element={<Premium />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>

        <Footer dark={effectiveDark} />
      </div>
    </>
  );
}

export default function App() {
  return (
    <Router>
      <UniverseProvider>
        <AuthProvider>
          <ProgressionProvider>
            <ParcoursProvider>
              <AssistProvider>
                <BetaAccessGate />
                <AppLayout />
              </AssistProvider>
            </ParcoursProvider>
          </ProgressionProvider>
        </AuthProvider>
      </UniverseProvider>
    </Router>
  );
}
